import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/*
 * InnoPart motion runtime.
 *
 * Deliberately small. Two jobs:
 *
 *  1. Reveal — one IntersectionObserver watches every [data-reveal] element,
 *     adds .is-revealed once, then stops watching it. Nothing replays.
 *  2. Parallax — an opt-in ±8px shift on a handful of images, driven by a
 *     single rAF-throttled scroll handler that only runs while at least one
 *     parallax element is on screen.
 *
 * Both read their scroll root from @theme/scroll-container, because above
 * 990px Horizon scrolls `.page-wrapper` rather than the document. A default
 * observer root or a window scroll listener would do nothing there.
 *
 * The hero is not handled here at all — it is a CSS animation that starts at
 * first paint, so the fold never waits on this script.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const PARALLAX_RANGE = 8;

/* ------------------------------------------------------------ reveals */

/**
 * Applies the stagger delay for any element inside a [data-stagger] container.
 * @param {HTMLElement} root
 */
function assignStaggerDelays(root) {
  root.querySelectorAll('[data-stagger]').forEach((group) => {
    const step = Number.parseInt(group.getAttribute('data-stagger') || '', 10) || 80;
    const items = group.querySelectorAll(':scope > [data-reveal]');
    items.forEach((item, index) => {
      // Cap the ramp so a long list never leaves the last card waiting.
      const delay = Math.min(index, 6) * step;
      /** @type {HTMLElement} */ (item).style.setProperty('--ip-reveal-delay', `${delay}ms`);
    });
  });

  // The group diagram sequences its own nodes after the spine has drawn.
  root.querySelectorAll('.group-intro__nodes').forEach((list) => {
    list.querySelectorAll(':scope > .group-intro__node').forEach((node, index) => {
      /** @type {HTMLElement} */ (node).style.setProperty('--ip-node-delay', `${420 + index * 90}ms`);
    });
  });
}

function initReveals() {
  const targets = document.querySelectorAll('[data-reveal]');
  if (!targets.length) return;

  if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  assignStaggerDelays(document);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        // Reveal once, then stop paying for it.
        observer.unobserve(entry.target);
      });
    },
    {
      root: getIntersectionRoot(),
      // Start slightly before the element is fully on screen so the motion
      // completes as it settles into view rather than after.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.15,
    }
  );

  targets.forEach((el) => {
    // Anything already on screen at load reveals immediately — no fade-in of
    // content the visitor is looking at.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      el.classList.add('is-revealed');
      return;
    }
    observer.observe(el);
  });
}

/* ----------------------------------------------------------- parallax */

function initParallax() {
  /** @type {NodeListOf<HTMLElement>} */
  const layers = document.querySelectorAll('[data-parallax]');
  if (!layers.length || REDUCED_MOTION.matches) return;

  /** @type {Set<HTMLElement>} */
  const active = new Set();
  let frame = 0;
  let scrollTarget = getScrollEventTarget();

  const render = () => {
    frame = 0;
    const viewport = window.innerHeight;
    active.forEach((layer) => {
      const rect = layer.getBoundingClientRect();
      // -1 above centre, +1 below. Read once per element per frame; nothing
      // here writes a value another element then reads back.
      const progress = (rect.top + rect.height / 2 - viewport / 2) / (viewport / 2 + rect.height / 2);
      const offset = Math.max(-1, Math.min(1, progress)) * PARALLAX_RANGE;
      layer.style.setProperty('--ip-parallax-y', `${offset.toFixed(2)}px`);
    });
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(render);
  };

  const visibility = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const layer = /** @type {HTMLElement} */ (entry.target);
        if (entry.isIntersecting) {
          active.add(layer);
        } else {
          active.delete(layer);
          layer.style.removeProperty('--ip-parallax-y');
        }
      });

      // Only listen while something is actually on screen.
      if (active.size) {
        scrollTarget.addEventListener('scroll', schedule, { passive: true });
        schedule();
      } else {
        scrollTarget.removeEventListener('scroll', schedule);
      }
    },
    { root: getIntersectionRoot(), rootMargin: '10% 0px' }
  );

  layers.forEach((layer) => visibility.observe(layer));

  // The scroll container swaps between .page-wrapper and the document at
  // 990px, so the listener has to move with it.
  scrollContainerMediaQuery.addEventListener('change', () => {
    scrollTarget.removeEventListener('scroll', schedule);
    scrollTarget = getScrollEventTarget();
    if (active.size) scrollTarget.addEventListener('scroll', schedule, { passive: true });
  });
}

/* --------------------------------------------------------------- boot */

function init() {
  initReveals();
  initParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The theme editor re-renders sections in place; re-scan so new markup reveals.
document.addEventListener('shopify:section:load', () => {
  assignStaggerDelays(document);
  document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
});
